import React, { useState } from 'react';
import styled from 'styled-components';

function QuestionInteractionHeatmap({ data }) {
  const [hoveredCell, setHoveredCell] = useState(null);

  if (!data || data.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No interaction data available</EmptyMessage>
      </EmptyState>
    );
  }

  // Extract unique users and questions
  const users = [...new Set(data.map((d) => d.userName))];

  // Get unique questions with their data and sort by title (Question 1, Question 2, etc.)
  const uniqueQuestions = [];
  const seenQuestionIds = new Set();

  data.forEach((item) => {
    if (!seenQuestionIds.has(item.questionId)) {
      seenQuestionIds.add(item.questionId);
      uniqueQuestions.push({
        id: item.questionId,
        title: item.questionTitle
      });
    }
  });

  // Sort questions by extracting the number from "Question X" format
  uniqueQuestions.sort((a, b) => {
    const aMatch = a.title.match(/Question (\d+)/);
    const bMatch = b.title.match(/Question (\d+)/);

    if (aMatch && bMatch) {
      return parseInt(aMatch[1], 10) - parseInt(bMatch[1], 10);
    }

    // Fallback to alphabetical if not in "Question X" format
    return a.title.localeCompare(b.title);
  });

  const questionIds = uniqueQuestions.map((q) => q.id);

  // Create a lookup map for interaction counts
  const interactionMap = {};
  data.forEach((item) => {
    const key = `${item.userName}-${item.questionId}`;
    interactionMap[key] = item.interactionCount;
  });

  // Find max interaction count for color scaling
  const maxInteraction = Math.max(...data.map((d) => d.interactionCount), 1);

  // Function to get color based on interaction count (heat colors)
  const getColor = (count) => {
    if (count === 0) return '#f9fafb'; // Very light gray (no interaction)
    const intensity = count / maxInteraction;
    if (intensity <= 0.2) return '#fef3c7'; // Light yellow
    if (intensity <= 0.4) return '#fde047'; // Yellow
    if (intensity <= 0.6) return '#fb923c'; // Orange
    if (intensity <= 0.8) return '#f97316'; // Dark orange
    return '#dc2626'; // Red (hot)
  };

  // Truncate long question titles
  const truncateTitle = (title, maxLength = 30) =>
    title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;

  return (
    <Container>
      <ScrollContainer>
        <HeatmapGrid $userCount={users.length} $questionCount={questionIds.length}>
          {/* Top-left corner cell */}
          <CornerCell />

          {/* Question headers */}
          {questionIds.map((questionId) => {
            const questionData = data.find((d) => d.questionId === questionId);
            return (
              <QuestionHeader key={questionId} title={questionData.questionTitle}>
                {truncateTitle(questionData.questionTitle, 25)}
              </QuestionHeader>
            );
          })}

          {/* User rows */}
          {users.map((userName) => (
            <React.Fragment key={userName}>
              {/* User name cell */}
              <UserCell>{userName}</UserCell>

              {/* Interaction cells for this user */}
              {questionIds.map((questionId) => {
                const key = `${userName}-${questionId}`;
                const count = interactionMap[key] || 0;
                const questionData = data.find((d) => d.questionId === questionId);

                return (
                  <InteractionCell
                    key={key}
                    $color={getColor(count)}
                    $isHovered={hoveredCell === key}
                    onMouseEnter={() => setHoveredCell(key)}
                    onMouseLeave={() => setHoveredCell(null)}
                    title={`${userName} - ${questionData.questionTitle}: ${count} interaction${count !== 1 ? 's' : ''}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </HeatmapGrid>
      </ScrollContainer>

      <Legend>
        <LegendTitle>Interaction Intensity:</LegendTitle>
        <LegendItems>
          <LegendItem>
            <LegendColor $color="#f9fafb" />
            <LegendText>None (0)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendColor $color="#fef3c7" />
            <LegendText>Low (1-20%)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendColor $color="#fde047" />
            <LegendText>Medium (20-40%)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendColor $color="#fb923c" />
            <LegendText>High (40-60%)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendColor $color="#f97316" />
            <LegendText>Very High (60-80%)</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendColor $color="#dc2626" />
            <LegendText>Maximum (80-100%)</LegendText>
          </LegendItem>
        </LegendItems>
      </Legend>
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0 0;
  width: 100%;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: auto;
  padding: 0 1rem 1rem;

  @media (min-width: 768px) {
    padding: 0 1.5rem 1.5rem;
  }
`;

const HeatmapGrid = styled.div`
  display: grid;
  gap: 2px;
  grid-template-columns: 150px repeat(${(props) => props.$questionCount}, minmax(100px, 1fr));
  min-width: fit-content;

  @media (max-width: 767px) {
    grid-template-columns: 120px repeat(${(props) => props.$questionCount}, minmax(80px, 1fr));
  }
`;

const CornerCell = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
`;

const QuestionHeader = styled.div`
  align-items: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: var(--brown);
  display: flex;
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 0.7rem;
    padding: 0.5rem 0.25rem;
  }
`;

const UserCell = styled.div`
  align-items: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  color: var(--brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.75rem 1rem;

  @media (max-width: 767px) {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem;
  }
`;

const InteractionCell = styled.div`
  align-items: center;
  background-color: ${(props) => props.$color};
  border: 1px solid ${(props) => (props.$isHovered ? 'var(--blue)' : '#e5e7eb')};
  cursor: pointer;
  display: flex;
  justify-content: center;
  min-height: 50px;
  transition: all 0.2s ease;

  &:hover {
    border-width: 2px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
    z-index: 10;
  }

  @media (max-width: 767px) {
    min-height: 40px;
  }
`;

const CellValue = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;

  @media (max-width: 767px) {
    font-size: 0.75rem;
  }
`;

const Legend = styled.div`
  background: #f9fafb;
  border-radius: 6px;
  margin-top: 1rem;
  padding: 1rem 1.5rem;
`;

const LegendTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const LegendItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 767px) {
    gap: 0.5rem;
  }
`;

const LegendItem = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const LegendColor = styled.div`
  background-color: ${(props) => props.$color};
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  height: 16px;
  width: 16px;
`;

const LegendText = styled.span`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;

  @media (max-width: 767px) {
    font-size: 0.75rem;
  }
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

export default QuestionInteractionHeatmap;
